import { getSettings, saveSettings, subscribeSettings } from '../storage/settings';
import type { AnalyticsConsent } from '../types/settings';
import { applyConsentChange, effectiveConsent, isOptedIn, patternAnonId } from './consent';
import { getJurisdictionTier } from './jurisdiction';
import { initPostHog, track as phTrack, setOptedIn, deleteMyData as phDelete } from './posthog';
import { initSentry, captureException as sentryCapture } from './sentry';
import type { EventName, EventProps } from './events';
import { useEffect, useState } from 'react';

let bootPromise: Promise<void> | null = null;

// VITE_ENVIRONMENT is set by deploy.sh ('devel' or 'production'). Falls back
// to MODE for `npm run dev` (yields 'development').
function detectEnvironment(): string {
  return import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;
}

function getSuperProps(language?: string): Record<string, unknown> {
  const lang = language ?? (typeof navigator !== 'undefined' ? navigator.language : undefined) ?? 'en';
  const environment = detectEnvironment();
  if (typeof matchMedia === 'undefined') return { language: lang, environment };
  const viewportClass =
    matchMedia('(max-width: 600px)').matches ? 'mobile' :
    matchMedia('(max-width: 1024px)').matches ? 'tablet' : 'desktop';
  return {
    viewport_class: viewportClass,
    pwa_installed: matchMedia('(display-mode: standalone)').matches,
    prefers_reduced_motion: matchMedia('(prefers-reduced-motion: reduce)').matches,
    language: lang,
    environment,
  };
}

async function ensureSdks(distinctId: string, language?: string): Promise<void> {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const release = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
  const tasks: Promise<void>[] = [];
  if (apiKey) tasks.push(initPostHog({
    apiKey,
    distinctId,
    appVersion: release,
    superProps: getSuperProps(language),
    optedInOnInit: true,
  }));
  if (dsn) tasks.push(initSentry({ dsn, release, userId: distinctId, environment: detectEnvironment() }));
  await Promise.all(tasks);
}

export async function initAnalytics(): Promise<void> {
  if (bootPromise) return bootPromise;
  bootPromise = (async () => {
    const settings = await getSettings();

    if (isOptedIn(settings.analyticsConsent) && settings.analyticsAnonId) {
      await ensureSdks(settings.analyticsAnonId, settings.language);
      setOptedIn(true);
      return;
    }

    if (settings.analyticsConsent !== 'pending') return;

    if (typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true) {
      return;
    }

    const tier = await getJurisdictionTier();
    if (tier !== 'optional-consent') return;

    const { next } = applyConsentChange(settings, 'auto-granted');
    await saveSettings(next);
    if (!next.analyticsAnonId) return;
    await ensureSdks(next.analyticsAnonId, next.language);
    setOptedIn(true);
    // Fire AFTER opt-in so the event is actually captured.
    track('analytics_toggled', { to: 'auto-granted' });
  })();
  return bootPromise;
}

export async function setConsent(to: AnalyticsConsent): Promise<void> {
  const current = await getSettings();
  const { next, action } = applyConsentChange(current, to);
  await saveSettings(next);
  if (action === 'enable' && next.analyticsAnonId) {
    await ensureSdks(next.analyticsAnonId, next.language);
    setOptedIn(true);
    // Fire AFTER opt-in so the event is actually captured.
    track('analytics_toggled', { to: next.analyticsConsent === 'auto-granted' ? 'auto-granted' : 'granted' });
  } else if (action === 'disable') {
    // Fire BEFORE opt-out so the event isn't dropped.
    track('analytics_toggled', { to: 'declined' });
    setOptedIn(false);
  }
}

export function track<N extends EventName>(name: N, props: EventProps<N>): void {
  phTrack(name, props);
}

export function captureException(err: unknown, context?: Record<string, unknown>): void {
  sentryCapture(err, context);
}

export async function deleteMyData(): Promise<void> {
  await phDelete();
}

export async function getPatternAnonId(patternId: string): Promise<string | null> {
  const s = await getSettings();
  if (!s.analyticsInstallSalt) return null;
  return patternAnonId(s.analyticsInstallSalt, patternId);
}

// Initial state is `null` (= "not loaded yet"), not `'pending'`. Returning
// `'pending'` on first paint would flash the consent banner for every visitor
// whose stored state is actually granted/declined/auto-granted, because
// IndexedDB is async-only. Callers treat `null` as "unknown" — banner stays
// hidden, settings toggle reads as off — until the real value resolves.
export function useConsent(): AnalyticsConsent | null {
  const [consent, setLocal] = useState<AnalyticsConsent | null>(null);
  useEffect(() => {
    let cancelled = false;
    getSettings().then((s) => { if (!cancelled) setLocal(effectiveConsent(s.analyticsConsent)); });
    const unsub = subscribeSettings(() => {
      getSettings().then((s) => { if (!cancelled) setLocal(effectiveConsent(s.analyticsConsent)); });
    });
    return () => { cancelled = true; unsub(); };
  }, []);
  return consent;
}

/** @internal — only for tests. */
export function resetForTest(): void {
  bootPromise = null;
}

export type { EventName, EventProps } from './events';
